import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Upload, FileText, Image as ImgIcon, Mail, Loader2, X, AlertCircle } from "lucide-react";

const DOC_TYPES = [
 ["lease", "Lease"],
 ["entry_report", "Entry condition"],
 ["exit_report", "Exit condition"],
 ["email", "Email"],
 ["screenshot", "Screenshot"],
 ["photo", "Photo"],
 ["invoice", "Invoice / Receipt"],
 ["notice", "Notice"],
 ["other", "Other"],
];

const SOURCE_LABEL = {
 user: "you set this",
 email_header: "from email header",
 pdf_content: "from PDF text",
 filename: "from filename",
 exif: "from photo EXIF (forensic)",
 inferred: "AI guessed",
 file_modified: "from file modified date",
};

/**
 * BulkImportModal. drag/select many files at once. Server returns the new
 * documents with inferred event_date + doc_type. User reviews the grid, edits
 * dates/types inline, then closes.
 */
export default function BulkImportModal({ caseId, onClose, onDone, initialResult }) {
 const [stage, setStage] = useState(initialResult ? "review" : "pick"); // 'pick' | 'uploading' | 'review'
 const [created, setCreated] = useState(initialResult?.created || []);
 const [skipped, setSkipped] = useState(initialResult?.skipped || []);
 const [progress, setProgress] = useState(0);
 const fileRef = useRef();

 const upload = async (files) => {
 if (!files || files.length === 0) return;
 setStage("uploading");
 setProgress(0);
 const fd = new FormData();
 for (const f of files) fd.append("files", f);
 try {
 const { data } = await api.post(`/cases/${caseId}/documents/bulk`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 onUploadProgress: (e) => {
 if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
 },
 timeout: 600000,
 });
 setCreated(data.created || []);
 setSkipped(data.skipped || []);
 setStage("review");
 } catch (e) {
 alert(e.response?.data?.detail || "Bulk upload failed");
 setStage("pick");
 }
 };

 const updateDoc = async (id, patch) => {
 try {
 await api.put(`/documents/${id}/metadata`, patch);
 setCreated(cs => cs.map(c => c.id === id ? {...c,...patch } : c));
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't save");
 }
 };

 const remove = async (id) => {
 if (!window.confirm("Remove this document?")) return;
 await api.delete(`/documents/${id}`);
 setCreated(cs => cs.filter(c => c.id !== id));
 };

 const finish = () => {
 onDone?.();
 onClose();
 };

 return (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={onClose}>
 <div onClick={e => e.stopPropagation()} className="bg-white max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl" data-testid="bulk-import-modal">
 <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E4DE]">
 <div>
 <div className="label-mono mb-0.5">Bulk import</div>
 <h2 className="font-serif text-2xl text-[#0D2B52]">Onboard everything at once</h2>
 </div>
 <button onClick={onClose} className="btn-ghost p-2" data-testid="bulk-close"><X size={18} strokeWidth={1.5} /></button>
 </div>

 <div className="flex-1 overflow-y-auto px-6 py-5">
 {stage === "pick" && (
 <div
 className="border-2 border-dashed border-[#E5E4DE] bg-[#FAF9F6] hover:border-[#1B6C62] p-14 text-center cursor-pointer transition-colors"
 onClick={() => fileRef.current?.click()}
 onDragOver={(e) => e.preventDefault()}
 onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files || [])); }}
 data-testid="bulk-dropzone"
 >
 <Upload size={32} strokeWidth={1.5} className="mx-auto text-[#0D2B52] mb-3" />
 <p className="font-serif text-xl text-[#1A1A1A] mb-2">Drop multiple files here</p>
 <p className="text-sm text-[#6B7280] mb-1">PDFs, photos, emails (.eml), screenshots, anything. Select as many as you like.</p>
 <p className="text-xs text-[#6B7280] italic font-serif">We'll guess the date from each file's content. EXIF is captured but never used as the chronology date (use Forensic Check for that).</p>
 <input ref={fileRef} type="file" multiple className="hidden"
 onChange={(e) => upload(Array.from(e.target.files || []))}
 data-testid="bulk-file-input" />
 </div>
 )}

 {stage === "uploading" && (
 <div className="text-center py-16">
 <Loader2 size={36} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-4 animate-spin" />
 <p className="font-serif text-lg text-[#0D2B52] mb-1">Uploading & reading your files…</p>
 <p className="text-sm text-[#6B7280]">{progress}% uploaded. Hang on. we're extracting dates and content.</p>
 </div>
 )}

 {stage === "review" && (
 <div>
 <div className="bg-[#E6F5F2] border border-[#1B6C62]/30 p-3 mb-5 text-sm text-[#0D2B52]" data-testid="bulk-review-banner">
 <strong>{created.length}</strong> file{created.length === 1 ? "" : "s"} imported. Quickly check the dates and types below. anything missing a date will sit at the bottom of your chronology until you assign one.
 {skipped.length > 0 && <span className="block text-[#B35D4C] mt-1"><AlertCircle size={12} className="inline mr-1" strokeWidth={1.75} />{skipped.length} skipped: {skipped.map(s => s.name).join(", ")}</span>}
 </div>

 <div className="grid sm:grid-cols-2 gap-3" data-testid="bulk-review-grid">
 {created.map(d => <ReviewTile key={d.id} doc={d} onUpdate={updateDoc} onRemove={remove} />)}
 </div>
 </div>
 )}
 </div>

 {stage === "review" && (
 <div className="px-6 py-4 border-t border-[#E5E4DE] flex justify-end gap-3">
 <button onClick={onClose} className="btn-ghost text-sm" data-testid="bulk-cancel">Add more later</button>
 <button onClick={finish} className="btn-primary text-sm" data-testid="bulk-done">Done. Open chronology</button>
 </div>
 )}
 </div>
 </div>
 );
}

function ReviewTile({ doc, onUpdate, onRemove }) {
 const [date, setDate] = useState(doc.event_date?.slice(0, 10) || "");
 const [type, setType] = useState(doc.doc_type || "other");

 const isImage = (doc.mime_type || "").startsWith("image/");
 const Icon = isImage ? ImgIcon : (doc.doc_type === "email" ? Mail : FileText);
 const source = doc.date_source;

 return (
 <div className="border border-[#E5E4DE] bg-white p-3 flex gap-3" data-testid={`review-tile-${doc.id}`}>
 <div className="w-10 h-10 bg-[#FAF9F6] border border-[#E5E4DE] flex items-center justify-center flex-shrink-0">
 <Icon size={18} strokeWidth={1.5} className="text-[#0D2B52]" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="font-medium text-sm text-[#1A1A1A] truncate" title={doc.original_name}>{doc.original_name}</div>
 <div className="text-[10px] text-[#6B7280] font-mono mb-2">{(doc.size_bytes / 1024).toFixed(0)} KB</div>

 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="label-mono block mb-0.5">Date</label>
 <input type="date" value={date}
 onChange={e => setDate(e.target.value)}
 onBlur={() => date !== (doc.event_date?.slice(0,10) || "") && onUpdate(doc.id, { event_date: date || null })}
 className="input-field text-xs py-1" data-testid={`review-date-${doc.id}`} />
 {source && date && (
 <div className="text-[9.5px] text-[#6B7280] mt-0.5 italic">{SOURCE_LABEL[source] || source}</div>
 )}
 {!date && (
 <div className="text-[9.5px] text-[#B35D4C] mt-0.5 italic">no date yet. Set one</div>
 )}
 </div>
 <div>
 <label className="label-mono block mb-0.5">Type</label>
 <select value={type}
 onChange={e => { setType(e.target.value); onUpdate(doc.id, { doc_type: e.target.value }); }}
 className="input-field text-xs py-1" data-testid={`review-type-${doc.id}`}>
 {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
 </select>
 </div>
 </div>
 </div>
 <button onClick={() => onRemove(doc.id)} className="btn-ghost p-1.5 self-start hover:text-[#B35D4C]" title="Remove" data-testid={`review-remove-${doc.id}`}>
 <X size={14} strokeWidth={1.5} />
 </button>
 </div>
 );
}
